<template>
  <Sheet
    :visible="visible"
    :title="$t('Campaign Notes')"
    :width="700"
    @close="visible = false"
    padded
  >
    <div id="NotesModal">
      <Card>
        <CardSection>
          <TextField v-model="newNote" :disabled="saving" multiline>
            <template #right>
              <Button 
                type="primary" 
                @click="saveNewNote" 
                :disabled="disableSave"
              >{{$t('Add Note')}}</Button>
            </template>
          </TextField>
          <Checkbox 
            :label="$t('CampaignNotes.groupChanges')" 
            :checked="groupChanges" 
            :helpText="groupChanges ? $t('CampaignNotes.groupChangesMessage') : null"
            @change="flipGroupChanges"
          />
        </CardSection>
      </Card>
      <Card>
        <Filters :query.sync="filter"/>
        <template v-if="selectedTab === 'ALL' && !filter">
          <Icon v-if="Object.values(groupNoteIdMap).find(v => !!v)" class="chevron" icon="fas fa-chevron-up" @click="setShowAll(false)"/>
          <Icon v-else class="chevron" icon="fas fa-chevron-down" @click="setShowAll(true)"/>
        </template>
        <Tabs
          :tabs="tabs"
          v-model="selectedTab"
          style="box-shadow: none;"
        />
        <div class="Modal-notes">
          <div v-if="loading" class="Modal-notes-none">{{$t('Loading')}} ...</div>
          <div v-if="!loading && filteredNotes.length === 0" class="Modal-notes-none">{{$t('Nothing to see here')}} ...</div>
          <template v-if="filteredNotes.length">
            <transition-group :name="'slide'">
              <template v-for="note in filteredNotes">
                <div
                  v-if="!note.groupNoteId || (groupNoteIdMap[note.groupNoteId]) || selectedTab !== 'ALL' || filter !== ''"
                  class="note"
                  :class="{'group-note': isGroupNote(note) && groupNoteIdMap[note.id] && selectedTab === 'ALL' && filter === ''}"
                  :key="note.id"
                >
                  <div 
                    class="head"
                    :class="{ 'within-group-note': note.groupNoteId && selectedTab === 'ALL' && filter === ''}"
                  >
                    <span class="type">
                      <div v-if="groupChanges && note.id > lastGroupNoteId" class="note-radio" @click="setSelected(note.id)">
                        <RadioButton :trueValue="true" :value="selectedNotes[note.id]"/>
                      </div>
                      <router-link 
                        v-if="getObjectLink(note) && note.objectType === 'INCENTIVE'" 
                        :to="getObjectLink(note)" 
                        tag="a" 
                        target="_blank">#{{ note.objectId }} — {{ getNoteTypeName(note) }}</router-link>
                      <router-link 
                        v-else-if="getObjectLink(note)" 
                        :to="getObjectLink(note)" 
                        tag="a" 
                        target="_blank">{{ getNoteTypeName(note) }}</router-link>
                      <span v-else>{{ getNoteTypeName(note) }}</span>
                      <div><Caption>{{ getNoteType(note) }} {{ note.groupNoteId ? `— ${getGroupNoteTitle(note.groupNoteId)}` : '' }}</Caption></div>
                    </span>

                    <div class="note-info">
                      <Formatter class="note-date" type="dateTimeShort" :value="note.createdOn" />
                      <div class="note-author">{{ note.createdByName }}</div>
                    </div>
                    <span v-if="isGroupNote(note) && selectedTab === 'ALL' && filter === ''" class="note-chevron">
                      <Icon v-if="groupNoteIdMap[note.id]" icon="fas fa-chevron-up" @click="setShowGroupNoteId(note.id, false)"/>
                      <Icon v-else icon="fas fa-chevron-down" @click="setShowGroupNoteId(note.id, true)"/>
                    </span>
                    <span v-if="showDelete(note)" class="delete">
                      <Icon @click="deleteNote(note)" icon="fas fa-times"/>
                    </span>
                  </div>
                  <div class="details" :class="{ 'within-group-note': note.groupNoteId && selectedTab === 'ALL' && filter === '' }">
                    <CampaignNotesManualNote v-if="note.objectType === 'NOTES'" :note="note"/>
                    <CampaignNotesSegment v-else-if="note.objectType === 'SEGMENT'" :note="note" :campaignId="campaignId"/>
                    <CampaignNotesBasic v-else :note="note"/>
                  </div>
                  <!-- <pre style="width: 100%;
                    max-width: 590px;
                    overflow-x: scroll;">{{note.notes}}</pre> -->
                </div>
              </template>
            </transition-group>
          </template>
        </div>
        <div v-if="!loading && notes.length && notes.length >= limit" class="footer">
          <Button v-if="!loadingMore" :fullWidth="true" @click="loadMoreNotes">{{$t('CampaignNotes.Load More')}}</Button>
          <SkeletonBodyText v-else/>
        </div>
      </Card>
    </div>

  </Sheet>
</template>

<script>
import CampaignNotesManualNote from './CampaignNotesManualNote'
import CampaignNotesSegment from './CampaignNotesSegment'
import CampaignNotesBasic from './CampaignNotesBasic'
const ELEMENTS_SHOWN = 50

function parseNotes(notes) {
  const rv = []
  if (!notes) return rv
  for (const note of notes) {
    let n = note
    try {
      const obj = JSON.parse(note.notes)
      n.notes = obj
    } catch (e) {
      rv.push(note)
      continue
    }

    // group notes
    if (note.objectType === 'NOTES') {
      note.notes.noteIds.forEach((strId) => {
        const foundNote = notes.find((item) => item.id === parseInt(strId))
        if (foundNote) {
          foundNote.groupNoteId = note.id
        }
      })
    }

    if (note.objectType === 'SEGMENT' && note.objectAction === 'UPDATE') {
      const noteObjectAddition = {}
      n.notes.forEach((noteInner) => {
        if (noteInner.attribute === 'RULE') {
          if (!noteObjectAddition['RULE']) {
            noteObjectAddition['RULE'] = []
          }
          noteObjectAddition['RULE'].push(noteInner)
        } else if (noteInner.attribute.toLowerCase().substring(0, 5) === 'offer') {
          if (!noteObjectAddition['OFFER']) {
            noteObjectAddition['OFFER'] = []
          }
          noteObjectAddition['OFFER'].push(noteInner)
        } else {
          if (!noteObjectAddition['OTHER']) {
            noteObjectAddition['OTHER'] = []
          }
          noteObjectAddition['OTHER'].push(noteInner)
        }
      })
      n.notes = {
        ...n.notes,
        ...noteObjectAddition
      }
    }
    rv.push(n)
  }
  // console.log(rv)
  return rv
}

export default {
  name: 'CampaignNotesSheet',
  data() {
    return {
      visible: false,
      notes: [],
      loading: true,
      loadingMore: false,
      selectedTab: 'ALL',
      filter: '',
      newNote: '',
      campaignId: null,
      // options: {},
      saving: false,
      // initialsColor: {},
      selectedNotes: {},
      sessionKey: null,
      accountRulesets: null,
      groupChanges: false,
      groupNoteIdMap: {},
      limit: ELEMENTS_SHOWN
    }
  },
  created() {
    this.eventHub.$on('showCampaignNotes', (campaignId, type = 'ALL') => {
      this.visible = true
      this.limit = ELEMENTS_SHOWN
      this.filter = ''
      this.groupChanges = false
      this.selectedNotes = {}
      this.groupNoteIdMap = {}
      this.selectedTab = type
      this.campaignId = campaignId
      this.$watch('selectedTab', this.reloadNotes)
      NProgress.start()
      Promise.all([
        this.api.getCampaignNotes(this.campaignId, this.limit, this.selectedTab),
        this.api.getAccountRulesetsForSegmentation(),
        this.api.getSessionKey()
      ])
        .then((data) => { 
          const [notes, accountRulesets, sessionKey] = data
          this.notes = parseNotes(notes.data || notes)
          this.accountRulesets = accountRulesets
          this.sessionKey = sessionKey

          window.RulesetEditorLoader
            .load()
            .then(lib => {
              this.$options.components.RuleSummary = lib.createRuleSummaryComponent({
                locale: this.theUser.defaultLanguage,
                authKey: this.sessionKey,
                campaignId: this.campaignId,
                accountRulesets: this.accountRulesets
              })
              this.loading = false 
            })
        })
        .catch(this.apiCatch)
        .finally(() => {
          NProgress.done()
        })
    })
  },
  beforeDestroy() {
    this.eventHub.$off('showCampaignNotes')
  },
  computed: {
    tabs() {
      return [
        { id: 'ALL', label: this.$t('All') },
        { id: 'NOTES', label: this.$t('Notes') },
        { id: 'CAMPAIGN', label: this.$t('Campaign') },
        { id: 'INCENTIVE', label: this.$t('Offer') },
        { id: 'SEGMENT', label: this.$t('Segment') },
        { id: 'DOMAIN', label: this.$t('Domain') },
        { id: 'ABTESTING', label: this.$t('A/B Testing') }
      ]
    },
    filteredNotes() {
      let notes = this.notes
      if (this.selectedTab) {
        if (this.selectedTab === 'ALL') {
          notes = this.notes.filter(note => note.objectType !== 'ABTESTING')
        } else {
          notes = this.notes.filter(note => note.objectType === this.selectedTab)
        }
      }
      if (this.filter) {
        const filter = this.filter.toLowerCase()
        notes = notes.filter((note) => {
          if (note.createdByName.toLowerCase().indexOf(filter) > -1) {
            return true
          }
          if (typeof note.notes === 'string') {
            if (note.notes.toLowerCase().indexOf(filter) > -1) {
              return true
            }
          } else {
            if (JSON.stringify(note.notes).toLowerCase().indexOf(filter) > -1) {
              return true
            }
          }
          if (note.objectName.toLowerCase().indexOf(filter) > -1) {
            return true
          }
          return false
        })
      }
      return notes
    },
    lastGroupNoteId() {
      for (const n of this.notes) {
        if (this.isGroupNote(n)) {
          return n.id
        }
      }
      return null
    },
    disableSave() {
      return this.saving || 
        !this.newNote || 
        (this.newNote && this.newNote.charAt(0) === '{') || 
        (this.newNote && this.newNote.charAt(0) === '[') ||
        (this.groupChanges && this.selectedNotes === {})
    }
  },
  methods: {
    reloadNotes() {
      this.loading = true
      this.notes = []
      this.api.getCampaignNotes(this.campaignId, this.limit, this.selectedTab)
        .then((response) => {
          this.notes = parseNotes(response.data || response)
          this.loading = false
        })
        .catch(this.apiCatch)
    },
    loadMoreNotes() {
      this.limit += ELEMENTS_SHOWN
      this.loadingMore = true
      this.api.getCampaignNotes(this.campaignId, this.limit, this.selectedTab)
        .then((response) => { 
          this.notes = parseNotes(response.data || response)
          this.loadingMore = false
        })
        .catch(this.apiCatch)
    },
    getNoteTypeIcon(note) {
      if (note.objectType === 'NOTES') {
        return 'far fa-edit'
      }
      if (note.objectType === 'INCENTIVE') {
        return 'fas fa-tag'
      }
      if (note.objectType === 'CAMPAIGN') {
        return 'fas fa-star'
      }
      if (note.objectType === 'SEGMENT') {
        return 'fas fa-list-ul'
      }
      if (note.objectType === 'DOMAIN') {
        return 'fas fa-link'
      }
      if (note.objectType === 'ABTESTING') {
        return 'fas fa-percentage'
      }
      return 'fas fa-question'
    },
    getObjectLink(note) {
      if (note.objectType === 'INCENTIVE') {
        return `/campaigns/${this.campaignId}/offers/edit/${note.objectId}`
      }
      if (note.objectType === 'CAMPAIGN') {
        return `/campaigns/${this.campaignId}/details/`
      }
      if (note.objectType === 'SEGMENT') {
        return `/campaigns/${this.campaignId}/segmentation/segment/${note.objectId}/`
      }
      if (note.objectType === 'DOMAIN') {
        return `/campaigns/${this.campaignId}/domains/`
      }
      if (note.objectType === 'ABTESTING') {
        return `/campaigns/${this.campaignId}/options`
      }
      return ''
    },
    getNoteTypeName(note) {
      if (this.isGroupNote(note)) {
        return this.$t('CampaignNotes.groupNote')
      }
      if (note.objectType === 'NOTES') {
        return this.$t('Manual')
      }
      if (note.objectType === 'INCENTIVE') {
        return note.objectName || this.$t('Incentive')
      }
      if (note.objectType === 'CAMPAIGN') {
        return note.objectName || this.$t('Campaign')
      }
      if (note.objectType === 'SEGMENT') {
        return note.objectName || this.$t('Segment')
      }
      if (note.objectType === 'DOMAIN') {
        return this.$t('Domain')
      }
      if (note.objectType === 'ABTESTING') {
        return note.objectName || this.$t('A/B Testing')
      }
      return this.$t('Unknown')
    },
    setSelected(id) {
      this.selectedNotes = {}
      for (const n of this.notes) {
        if (n.id >= id) {
          this.selectedNotes[n.id] = true
        } else {
          break
        }
      }
    },
    getGroupNoteTitle(id) {
      const groupNote = this.notes.find(note => note.id === id)
      if (groupNote) {
        return groupNote.notes.note
      }
      return ''
    },
    isGroupNote(note) {
      return note.objectType === 'NOTES' && typeof note.notes === 'object'
    },
    flipGroupChanges() {
      if (this.groupChanges) {
        this.selectedNotes = {}
        this.groupChanges = false
      } else {
        this.groupChanges = true
      }
    },
    setShowAll(val) {
      if (!val) {
        this.groupNoteIdMap = {}
        return
      }
      this.notes.forEach((n) => {
        if (this.isGroupNote(n)) {
          this.$set(this.groupNoteIdMap, n.id, true)
        }
      })
    },
    setShowGroupNoteId(id, val) {
      this.$set(this.groupNoteIdMap, id, val)
    },
    getNoteType(note) {
      const t = this.tabs.find(t => t.id === note.objectType)
      return t ? t.label : '?'
    },
    showDelete(note) {
      if (this.theUser && note.objectType === 'NOTES' && !note.groupNoteId) {
        const currDate = window.moment.tz(window.moment().utc(), 'UTC').tz(this.theUser.accountTimezone)
        if (currDate.diff(window.moment.tz(note.createdOn, this.theUser.accountTimezone), 'minutes') < 10) {
          return true
        }
      }
      return false
    },
    saveNewNote() {
      if (this.newNote.trim().length === 0) {
        return
      }
      NProgress.start()
      this.saving = true
      const selectedNotesArray = Object.keys(this.selectedNotes)
      this.api.addCampaignNote(this.campaignId, this.newNote, this.groupChanges ? selectedNotesArray : null)
        .then((newNoteId) => {
          let formattedNewNote = this.newNote
          if (this.groupChanges) {
            formattedNewNote = {
              note: this.newNote,
              noteIds: selectedNotesArray
            }
            selectedNotesArray.forEach((strId) => {
              const foundNote = this.notes.find((item) => item.id === parseInt(strId))
              if (foundNote) {
                foundNote.groupNoteId = newNoteId
              }
            })
          }
          // add to list
          this.notes.unshift({
            id: newNoteId,
            campaignId: this.campaignId,
            notes: formattedNewNote,
            objectType: 'NOTES',
            objectId: this.campaignId,
            userId: -1,
            accountId: -1,
            onBehalfOfAccountId: null,
            createdBy: -1,
            createdOn: window.moment.tz(window.moment.utc(), 'UTC').tz(this.theUser.accountTimezone).format('YYYY-MM-DD HH:mm:ss'),
            createdByName: this.$t('Me')
          })

          this.newNote = ''
          this.groupChanges = false
        })
        .catch(this.apiCatch)
        .finally(() => {
          NProgress.done()
          this.saving = false
        })
    },
    deleteNote(note) {
      NProgress.start()
      this.api.removeCampaignNote(this.campaignId, note)
        .then(() => {
          this.removeNoteFromList(note)
        })
        .catch(this.apiCatch)
        .finally(() => {
          NProgress.done()
        })
    },
    removeNoteFromList(note) {
      let noteIndex = -1
      this.notes.forEach((n, index) => {
        if (n.id === note.id) {
          noteIndex = index
        }
        if (n.groupNoteId === note.id) {
          n.groupNoteId = null
        }
      })
      if (noteIndex > -1) {
        this.notes.splice(noteIndex, 1)
      } else {
        this.$ui.alert(this.$t('CampaignNotes.Could not find note to remove'))
      }
    }

  },
  components: {
    CampaignNotesManualNote,
    CampaignNotesSegment,
    CampaignNotesBasic
  }
}
</script>

<style lang="scss">
#NotesModal {
  font-size: 14px;
  .chevron {
    float: right;
    padding-top: 18px;
    padding-right: 15px;
    cursor: pointer;
  }
  .Modal-notes {
    // height: 50vh;
    overflow: hidden;
    // overflow-y: scroll;
    .note-chevron {
      margin-left: 10px;
      cursor: pointer;
    }
    .delete {
      margin-left: 10px;
      margin-top: -2px;
      cursor: pointer;
      color: red;
    }

    .within-group-note {
      border-left: 8px solid #696969;
    }
    .group-note {
      border-left: 8px solid #696969;
    }
    .note {
      hr {
        width: 80%;
        margin-top: 10px;
        margin-bottom: 10px;
      }
      .head {
        display: flex;
        align-items: center;
        background: #f5f5f5;
        padding: 10px;
        border-top: 1px solid #ddd;
        .type {
          flex: 1;
          .note-radio {
            float: left;
          }
          i {
            color: #fff;
            background: #9999;
            font-size: 15px;
            width: 25px;
            height: 25px;
            border-radius: 50%;
            display: inline-block;
            padding: 5px;
            padding-left: 4px;
            margin-right: 5px;
          }
        }
        .note-info {
          color: #666;
          font-size: 80%;
          text-align: right;
        }
      }
      .details {
        padding: 15px;
        font-size: 110%;
        .note-group-section:first-of-type {
          margin-top: 0px;
        }
        .note-group-section {
          margin-top: 8px;
          .simple-note-content:first-of-type {
            margin-top: 0px;
          }
          .simple-note-content {
            margin-top: 4px;
          }
        }
        .rule:first-of-type {
          margin-top: 5px;
        }
        .rule {
          .delete-rule {
            margin-bottom: 1px;
            background: #fef0ef;
            border-left: 4px solid #f01800;
          }
          .new-rule {
            margin-bottom: 1px;
            background: #e9f3e9;
            border-left: 4px solid green;
          }
          .change-rule {
            margin-bottom: 1px;
            background: #edf6ff;
            border-left: 4px solid #026ebb;
          }
          .rule-text {
            font-size: 14px;
            padding: 8px 16px 8px 12px;
          }
        }
        .offer-list {
          margin-top: 5px;
        }
      }
    }
    .slide-enter-active {
      transition-duration: 0.3s;
      transition-timing-function: ease-in;
    }

    .slide-leave-active {
      transition-duration: 0.3s;
      transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
    }
    .slide-enter-to, .slide-leave {
      max-height: 100px;
      overflow: hidden;
    }
    .slide-enter, .slide-leave-to {
      overflow: hidden;
      max-height: 0;
    }
  }

  .footer {
    padding: 10px 15px;
    border-top: 1px solid #ccc;
  }
  .red {
    color: red;
  }

  .Modal-notes-none {
    text-align: center;
    margin-top: 30px;
    margin-bottom: 30px;
    font-size: 120%;
    color: #666;
  }
  table.change-table {
    margin-top: 5px;
    // margin-left: -9px;
    font-size: 90%;
    border-radius: 0px;
    box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.1);
    td {
      padding: 3px;
      vertical-align: top;
      line-height: 22px;
      text-align: left;
    }
  }
}
</style>
